// CIN-7133: shared service display helpers (price + duration) for the service
// picker, the Date & Time summary and the review step.
//
// The value is the resolver's `price_raw` (what the Google Reserve feed reads,
// backendsafe google_reserve/builders/service.py), else `price`. It is parsed
// exactly like the feed's `_to_decimal` (Python `Decimal(str(value))`, finite
// only), and a price is shown ONLY when it rounds to more than $0.00. Missing,
// '', '$45', '1,250.00', '0x10', 'Infinity', negative, boolean/list/object,
// zero and sub-cent values ('0.004') all render nothing. Zero is hidden per
// product decision (CIN-7133 scope change); the feed's matching
// STARTS_AT $0 -> NOT_DISPLAYED change is tracked separately on the backend.

export const STARTING_AT_LABEL = 'Starting At';

const isBlank = (value) => value === null || value === undefined || value === '';

// Raw price value for a service: price_raw, else price, else null. This only
// picks the field; formatServicePrice decides whether it is displayable.
// The `price` fallback only covers payloads without price_raw (older/cached
// responses); the feed reads price_raw alone, and the resolver sets both.
export const getServicePriceValue = (service) => {
  if (!isBlank(service?.price_raw)) return service.price_raw;
  if (!isBlank(service?.price)) return service.price;
  return null;
};

// Python's Decimal string grammar as CPython's C decimal applies it: surrounding
// whitespace is stripped and EVERY underscore is dropped before matching, so
// '1_000' parses as 1000. No '$', ',', hex or other prefixes. ('Infinity'/'NaN'
// parse in Python but are not finite, which the feed rejects too.)
const DECIMAL_STRING = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

// Mirror of the feed's _to_decimal: a finite number, or null.
const parsePrice = (value) => {
  let n;
  if (typeof value === 'number') {
    n = value;
  } else if (typeof value === 'string') {
    const cleaned = value.trim().replace(/_/g, '');
    if (!DECIMAL_STRING.test(cleaned)) return null;
    n = Number(cleaned);
  } else {
    // bool/list/object overrides: str() of these is not a Decimal.
    return null;
  }
  return Number.isFinite(n) ? n : null;
};

// Display string for a service price ('' = render nothing).
export const formatServicePrice = (service) => {
  const value = getServicePriceValue(service);
  if (value === null) return '';
  const n = parsePrice(value);
  if (n === null) return '';
  const text = n.toFixed(2);
  // Only a price that DISPLAYS as more than $0.00 is shown (hides 0 and sub-cent).
  if (!(Number(text) > 0)) return '';
  return `$${text}`;
};

// Human duration ('1 hour 30 mins') for a time estimate in minutes.
export const formatServiceDuration = (minutes) => {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${mins} ${mins === 1 ? 'min' : 'mins'}`;
  } else if (hours > 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  } else {
    return `${mins} ${mins === 1 ? 'min' : 'mins'}`;
  }
};
